import axios from 'axios';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { addCovidTestAnalyzer, editCovidTestAnalyzer } from 'services/covidTestAnalyzerServices';
import { AnalyzerTestMethod } from 'ts-types/api.enums';
import { TestLaboratoryDto, UpsertCovidTestAnalyzerDto } from 'ts-types/api.types';
import { required } from 'utils/validatorUtils';

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  covidTestAnalyzer?: UpsertCovidTestAnalyzerDto,
  covidTestAnalyzerId?: number,
  laboratories: TestLaboratoryDto[]
}

interface State {
  testMethodOptions: DropdownOption[],
  laboratoryOptions: DropdownOption[],
  errorMessage?: string,
}

class UpsertCovidTestAnalyzerForm extends Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  constructor(props: Props) {
    super(props);
    const {getMessage} = props;

    const testMethodOptions = stringArrayToDropdownOptionArray(
        Object.values(AnalyzerTestMethod), getMessage, "enum.AnalyzerTestMethod");

    const laboratoryOptions = mapToDropdownOptionArray(props.laboratories, "name", "id");


    this.state = {
      testMethodOptions: testMethodOptions,
      laboratoryOptions: laboratoryOptions
    }
  }

  handleSubmit = async (values: Partial<UpsertCovidTestAnalyzerDto>) => {

    let saveValues: Partial<UpsertCovidTestAnalyzerDto> = {...values};

    const {covidTestAnalyzerId, onSave} = this.props;
    const cancelTokenSource = this.cancelTokenSource;

    if (covidTestAnalyzerId) {
      await editCovidTestAnalyzer(covidTestAnalyzerId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    } else {
      await addCovidTestAnalyzer(saveValues, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    }
  };

  handleError = (error: any) => {
    const {getMessage} = this.props;
    this.setErrorMessage(getMessage("error.general"));
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  }

  render(): React.ReactNode {

    const {covidTestAnalyzer = {}} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={covidTestAnalyzer}
            subscription={{values: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {laboratoryOptions, testMethodOptions, errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            {errorMessage
                && (
                    <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                      {errorMessage}
                    </StyledErrorMessage>
                )
            }
            <PraxFormGrid>
              <Grid.Row>
                <Grid.Column width={5}>
                  {getMessage("covidTestAnalyzer.code")}
                </Grid.Column>
                <Grid.Column width={10}>
                  <Field
                      fluid
                      name="code"
                      component={Input}
                      validate={required}
                      autoFocus
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={5}>
                  {getMessage("covidTestAnalyzer.description")}
                </Grid.Column>
                <Grid.Column width={4}>
                  <Field
                      fluid
                      name="description"
                      component={Input}
                      validate={required}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={5}>
                  {getMessage("covidTestAnalyzer.laboratory")}
                </Grid.Column>
                <Grid.Column width={4}>
                  <Field
                      id="laboratoryId"
                      name="laboratoryId"
                      component={Select}
                      options={laboratoryOptions}
                      validate={required}
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={5}>
                  {getMessage("covidTestAnalyzer.testMethod")}
                </Grid.Column>
                <Grid.Column width={4}>
                  <Field
                      name="testMethod"
                      component={Select}
                      options={testMethodOptions}
                      validate={required}
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };
}

export default withMessages(UpsertCovidTestAnalyzerForm)
